import { observable, when, flow } from 'mobx';
import { getSnapshot } from 'mobx-state-tree';

import {
    getExperimentSegment,
    getRequestMetadata,
    startSavingDocumentChanges,
    DOCUMENTS,
} from '@ratehub/base-ui';
import { findOrCreateDocument } from '@ratehub/ins-common';

import getFormTypeFromInsuranceType from '../functions/getFormTypeFromInsuranceType';
import { InsLifeWizardInsuranceTypes } from '../definitions/CurrentDocument';
import EXPERIMENTS from '../definitions/Experiments';
import createViewStore from './createViewStore';


/**
 * @public
 * Create a new root context. Usually passed to RootComponent.
 * Can configure by passing in a config object (eg. to disable routing)

 * @param {Object} arg
 * @param {SessionStore} arg.sessionStore our source of truth
 * @returns {Object}
 */
function createContext({ sessionStore, brokers, insurers } = {}) {
    if (sessionStore == null) {
        throw new Error('Missing required parameter config.sessionStore');
    }

    // Private state; kept out of the public API.
    let _autoSaveDisposer = observable.box();

    // Create the context object.
    const context = observable({
        sessionStore,
        activeDocument: null,
        viewStore: null,

        brokers,
        insurers,

        hasError: false,
        isSubmittingLead: false,
        shouldPushNewLead: true,
        
        get isReady() {
            return context.areAllExperimentsReady 
                && context.viewStore != null;
        },
        get areAllExperimentsReady() {
            return Object.values(EXPERIMENTS).every(e => getExperimentSegment(e) !== null);
        },
        get isAutoSaveEnabled() {
            return !!_autoSaveDisposer.get();
        },
        get journey() {
            return context.activeDocument?.journey;
        },
        
        get formType() {
            return context.journey?.formType;
        },
        get insuranceType() {
            return context.journey?.insuranceType;
        },
        get lastEditDate() {
            return context.activeDocument.updatedAt ?? context.activeDocument.createdAt;
        },
        get provider() {
            return (
                context.brokers?.find(broker => broker.id === context.journey?.providerId)
                // In case our provider is an insurer
                || context.insurers?.find(insurer => insurer.id === context.journey?.providerId)
            );
        },
        
        updateJourney(snapshot) {
            context.journey.update(snapshot);
        },
        setIsAutoSaveEnabled(value) {
            if (value && !context.isAutoSaveEnabled) {
                _autoSaveDisposer.set(startSavingDocumentChanges(
                    context.sessionStore, 
                    context.activeDocument,
                    DOCUMENTS.INSURANCE_LIFE.PATH,
                ));
            } else if (!value && context.isAutoSaveEnabled) {
                // Dispose any existing auto save.
                _autoSaveDisposer.get()();
                _autoSaveDisposer.set(null);
            }
        },
        loadDocument: flow(function* (insuranceType) {
            // Default to term if invalid or not provided
            const validatedInsuranceType = Object.values(InsLifeWizardInsuranceTypes).includes(insuranceType)
                ? insuranceType
                : InsLifeWizardInsuranceTypes.TERM;

            const formType = getFormTypeFromInsuranceType(validatedInsuranceType);

            // Ensure session store is up to date before we get our document
            yield context.sessionStore.fetchSession();

            const defaultSnapshot = {
                journey: {
                    applicant: context.journey?.applicant ?? {}, // This is required for addProfileDataToDocumentSnapshot
                    insuranceType: validatedInsuranceType,
                    formType,
                    // Assume joint-policy if insurance type is joint-life
                    isJointPolicy: insuranceType === InsLifeWizardInsuranceTypes.JOINT_LIFE ? true : null,
                },
            };

            context.activeDocument = yield findOrCreateDocument(
                {
                    documentType: DOCUMENTS.INSURANCE_LIFE.TYPE,
                    defaultSnapshot,
                },
                context.sessionStore,
            );

            context.setIsAutoSaveEnabled(true);
        }),
        submitInquiry() {
            context.activeDocument.trackEvent({
                type: 'quote-inquiry',
                details: getSnapshot(context.journey),
                requestMetadata: getRequestMetadata(),
            });
        },
        setHasError(value) {
            context.hasError = value;
        },
        setIsSubmittingLead(value) {
            context.isSubmittingLead = value;
        },
        setShouldPushNewLead(value) {
            context.shouldPushNewLead = value;
        },
    });

    // Wait until all experiments are ready before continuing to load: simplifies assumptions during initialization.
    when(
        () => context.areAllExperimentsReady, 
        async () => {
            // Now that the Document is pulled-in, create the ViewStore.
            context.viewStore = createViewStore(context);
        },
    );

    return context;
}

export default createContext;
